import { useEffect, useState, useRef } from 'react';
import posthog from 'posthog-js';
import { analytics } from '../utils/analytics';

// Cache to track which experiments have been ensured this session
const ensuredExperiments = new Set<string>();

/**
 * Ensure experiment exists in PostHog before getting variant
 * This calls our server-side API which creates the experiment if it doesn't exist
 */
async function ensureExperimentExists(
    experimentName: string,
    variants: string[]
): Promise<boolean> {
    // Skip if already ensured this session
    const cacheKey = `${experimentName}:${variants.sort().join(',')}`;
    if (ensuredExperiments.has(cacheKey)) {
        return true;
    }

    try {
        const response = await fetch('/api/experiments/ensure', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                experimentName,
                variants,
            }),
        });

        if (response.ok) {
            const result = await response.json();
            if (result.success) {
                ensuredExperiments.add(cacheKey);
                if (result.created) {
                    console.log(
                        `[ABTest] Created experiment in PostHog: ${experimentName}`
                    );
                }
                return true;
            }
        }

        console.warn(
            `[ABTest] Failed to ensure experiment ${experimentName}:`,
            await response.text()
        );
        return false;
    } catch (error) {
        // Don't block on network errors - experiment might already exist
        console.warn(
            `[ABTest] Network error ensuring experiment ${experimentName}:`,
            error
        );
        return false;
    }
}

/**
 * Map PostHog's standard variant keys to the original variant names
 * PostHog experiments require "control", "test", "test_2", etc.
 * But our code uses descriptive names like "before_pairing", "after_pairing"
 */
function mapPostHogVariantToOriginal(
    posthogVariant: string,
    variants: string[]
): string | null {
    // PostHog naming convention: control=0, test=1, test_2=2, test_3=3, etc.
    if (posthogVariant === 'control' && variants.length > 0) {
        return variants[0];
    }
    if (posthogVariant === 'test' && variants.length > 1) {
        return variants[1];
    }

    // Handle test_N variants
    const testMatch = posthogVariant.match(/^test_(\d+)$/);
    if (testMatch) {
        const index = parseInt(testMatch[1], 10);
        if (index < variants.length) {
            return variants[index];
        }
    }

    // If the variant is already one of our original names, use it directly
    if (variants.includes(posthogVariant)) {
        return posthogVariant;
    }

    return null;
}

/**
 * Get variant from PostHog feature flag
 */
function getVariantFromPostHog(
    experimentName: string,
    variants: string[],
    defaultVariant: string
): string {
    // Try to get variant from PostHog feature flag
    const flagValue = posthog.getFeatureFlag(experimentName);

    // PostHog multivariate flags return the variant key directly
    if (flagValue && typeof flagValue === 'string') {
        // Map PostHog's control/test naming to our original variant names
        const mappedVariant = mapPostHogVariantToOriginal(flagValue, variants);
        if (mappedVariant) {
            return mappedVariant;
        }
    }

    // Also check payload for backward compatibility
    const payloadValue = posthog.getFeatureFlagPayload(experimentName);
    if (payloadValue && typeof payloadValue === 'string') {
        const mappedVariant = mapPostHogVariantToOriginal(
            payloadValue,
            variants
        );
        if (mappedVariant) {
            return mappedVariant;
        }
    }

    if (
        payloadValue &&
        typeof payloadValue === 'object' &&
        'variant' in payloadValue
    ) {
        const variantValue = (payloadValue as any).variant;
        if (typeof variantValue === 'string') {
            const mappedVariant = mapPostHogVariantToOriginal(
                variantValue,
                variants
            );
            if (mappedVariant) {
                return mappedVariant;
            }
        }
    }

    // Emergency fallback to hash-based assignment
    // This ensures users get a consistent experience even if PostHog is unavailable
    const userId = localStorage.getItem('pair_user_id') || 'anonymous';
    const hash = userId
        .split('')
        .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const variantIndex = hash % variants.length;
    return variants[variantIndex];
}

/**
 * Hook for A/B testing with automatic experiment creation and tracking
 *
 * This hook:
 * 1. Ensures the experiment exists in PostHog (creates if not)
 * 2. Gets the variant assignment from PostHog feature flags
 * 3. Tracks experiment exposure
 *
 * @param experimentName - Name of the A/B test experiment
 * @param variants - Array of variant names
 * @param defaultVariant - Default variant to use while loading
 * @returns The selected variant for this user
 *
 * @example
 * const variant = useABTest('ai_curation_prompt', ['control', 'variant_a', 'variant_b'], 'control');
 */
export function useABTest(
    experimentName: string,
    variants: string[],
    defaultVariant: string = variants[0]
): string {
    const [variant, setVariant] = useState<string>(defaultVariant);
    const [isReady, setIsReady] = useState<boolean>(false);

    // Use refs to ensure operations only happen once
    const hasEnsuredRef = useRef<boolean>(false);
    const hasTrackedRef = useRef<boolean>(false);

    useEffect(() => {
        if (typeof window === 'undefined' || !posthog) {
            return;
        }

        const initializeExperiment = async () => {
            try {
                // Step 1: Ensure experiment exists in PostHog (only once)
                if (!hasEnsuredRef.current) {
                    hasEnsuredRef.current = true;
                    await ensureExperimentExists(experimentName, variants);

                    // Reload feature flags after ensuring experiment exists
                    // This gives PostHog a chance to sync the new flag
                    posthog.reloadFeatureFlags();
                }

                // Step 2: Wait a moment for flags to load, then get variant
                // PostHog SDK should have flags loaded, but give it a moment
                setTimeout(() => {
                    const selectedVariant = getVariantFromPostHog(
                        experimentName,
                        variants,
                        defaultVariant
                    );

                    setVariant(selectedVariant);
                    setIsReady(true);

                    // Step 3: Track experiment exposure (only once)
                    if (!hasTrackedRef.current) {
                        hasTrackedRef.current = true;
                        analytics.capture('experiment_started', {
                            experiment_name: experimentName,
                            variant: selectedVariant,
                            variants_available: variants,
                        });
                    }
                }, 100);
            } catch (error) {
                console.warn(
                    `[ABTest] Error in experiment ${experimentName}:`,
                    error
                );
                setVariant(defaultVariant);
                setIsReady(true);
            }
        };

        initializeExperiment();
    }, [experimentName, variants, defaultVariant]);

    return variant;
}

/**
 * Hook to track conversion events for A/B tests
 * NOTE: Should be used with the variant from useABTest, not called separately
 *
 * @param experimentName - Name of the A/B test experiment
 * @param variant - The variant assigned to this user (from useABTest)
 * @returns Function to track conversion
 *
 * @example
 * const variant = useABTest('ai_curation_prompt', ['control', 'test'], 'control');
 * const trackConversion = useABTestConversion('ai_curation_prompt', variant);
 * // When user completes the desired action:
 * trackConversion('submitted');
 */
export function useABTestConversion(experimentName: string, variant: string) {
    const trackConversion = (
        conversionType: string = 'converted',
        properties?: Record<string, any>
    ) => {
        // Get session ID for linking to session recordings
        let sessionId: string | undefined;
        try {
            sessionId =
                posthog?.get_session_id?.() ||
                (posthog?.sessionRecording as any)?.sessionId ||
                undefined;
        } catch (e) {
            // Ignore errors getting session ID
        }

        analytics.capture('experiment_converted', {
            experiment_name: experimentName,
            variant,
            conversion_type: conversionType,
            // Include session ID for PostHog session recording linkage
            ...(sessionId && { $session_id: sessionId }),
            ...properties,
        });
    };

    return trackConversion;
}

/**
 * Hook for multivariate testing (MVT)
 *
 * @param testName - Name of the multivariate test
 * @param variables - Object with variable names and their possible values
 * @returns Object with selected values for each variable
 *
 * @example
 * const mvt = useMultivariateTesting('onboarding_flow', {
 *   buttonColor: ['blue', 'green', 'red'],
 *   buttonText: ['Get Started', 'Begin Now', 'Start Free'],
 *   headerStyle: ['minimal', 'detailed']
 * });
 * // Use: mvt.buttonColor, mvt.buttonText, mvt.headerStyle
 */
export function useMultivariateTesting<T extends Record<string, string[]>>(
    testName: string,
    variables: T
): Record<keyof T, string> {
    const [selectedValues, setSelectedValues] = useState<
        Record<keyof T, string>
    >(() => {
        // Initialize with first value of each variable
        const initial: any = {};
        Object.keys(variables).forEach((key) => {
            initial[key] = variables[key][0];
        });
        return initial;
    });

    useEffect(() => {
        if (typeof window !== 'undefined' && posthog) {
            try {
                // Get or generate consistent assignment
                const userId =
                    localStorage.getItem('pair_user_id') || 'anonymous';
                const selected: any = {};

                Object.keys(variables).forEach((variableName, index) => {
                    const values = variables[variableName];
                    // Use a different hash component for each variable
                    const hash = userId
                        .split('')
                        .reduce(
                            (acc, char) =>
                                acc + char.charCodeAt(0) * (index + 1),
                            0
                        );
                    const valueIndex = hash % values.length;
                    selected[variableName] = values[valueIndex];
                });

                setSelectedValues(selected);

                // Track MVT exposure
                analytics.capture('mvt_started', {
                    test_name: testName,
                    variables: selected,
                });
            } catch (error) {
                console.warn(`[MVT] Error in test ${testName}:`, error);
            }
        }
    }, [testName, variables]);

    return selectedValues;
}
